/**
 * Exact references shown shortened, one policy per kind. The short form is for reading only:
 * links, tooltips and copy buttons always carry the full value (NFR-16).
 *
 * - a versioned reference `<id>@<version>` (item, document or corpus) shows its version, as the
 *   mockups do (`@6685aa`);
 * - a Git object ID (commit or blob) shows its first characters, as Git does;
 * - an item ID shows the first group of its UUID.
 */
export type ShortReferenceKind = "version" | "gitObject" | "itemId";

const SHORT_LENGTH: Readonly<Record<ShortReferenceKind, number>> = {
  version: 6,
  gitObject: 7,
  itemId: 8,
};

const GIT_OBJECT_ID = /^[0-9a-f]{40}([0-9a-f]{24})?$/;

/** `reference` shortened for reading, by the policy of its kind. */
export function shortReference(kind: ShortReferenceKind, reference: string): string {
  const length = SHORT_LENGTH[kind];
  switch (kind) {
    case "version": {
      const at = reference.lastIndexOf("@");
      return (at < 0 ? reference : reference.slice(at + 1)).slice(0, length);
    }
    case "gitObject":
      // Anything that is not a Git object ID (a branch name, say) is shown as it is.
      return GIT_OBJECT_ID.test(reference) ? reference.slice(0, length) : reference;
    case "itemId": {
      const at = reference.indexOf("@");
      return (at < 0 ? reference : reference.slice(0, at)).slice(0, length);
    }
  }
}

/**
 * The local name of an IRI: what follows its last `#`, `/` or `:` (`ex#Monk` → `Monk`,
 * `urn:isbn:42` → `42`). An IRI that ends in a separator is shown whole.
 */
export function iriLocalName(iri: string): string {
  const cut = Math.max(iri.lastIndexOf("#"), iri.lastIndexOf("/"), iri.lastIndexOf(":"));
  const name = iri.slice(cut + 1);
  return name === "" ? iri : name;
}
